import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { authApi } from "../api/client";
import type { User } from "../api/types";
import { AUTH_STORAGE_KEY } from "../constants/auth";

type AuthContextValue = {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function loadStoredAuth(): { user: User | null; token: string | null } {
  if (typeof localStorage === "undefined") return { user: null, token: null };
  const raw = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!raw) return { user: null, token: null };
  try {
    const parsed = JSON.parse(raw) as { user: User | null; token: string | null };
    // Validate token format (JWT should have 3 parts)
    if (parsed.token && typeof parsed.token === "string") {
      const parts = parsed.token.split(".");
      if (parts.length !== 3) {
        console.warn("Invalid JWT format detected, clearing auth");
        localStorage.removeItem(AUTH_STORAGE_KEY);
        return { user: null, token: null };
      }
    }
    return parsed;
  } catch (err) {
    console.warn("Invalid auth cache", err);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return { user: null, token: null };
  }
}

function persistAuth(user: User | null, token: string | null) {
  if (typeof localStorage === "undefined") return;
  if (!user || !token) {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return;
  }
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ user, token }));
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [{ user, token }, setAuth] = useState(loadStoredAuth());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    persistAuth(user, token);
  }, [user, token]);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const result = await authApi.login(email, password);
      console.log("Login successful, token received:", result.token?.substring(0, 50) + "...");

      // Validate token format before storing
      if (!result.token || typeof result.token !== "string") {
        throw new Error("Invalid token received from server");
      }

      const parts = result.token.split(".");
      if (parts.length !== 3) {
        console.error("Invalid JWT format, parts:", parts.length);
        throw new Error("Invalid JWT token format");
      }

      console.log("Token validated successfully");
      setAuth({ user: result.user, token: result.token });
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setAuth({ user: null, token: null });
  };

  const value = useMemo(() => ({ user, token, loading, login, logout }), [user, token, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
